import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import api from "@/lib/api";
import { Printer, ArrowLeft, Check, X, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Printable AI Visibility Audit report — hand this to the prospect at the end of the call.
 * Also serves the public share-token view at /audit/public/:id/:token (no auth needed). */
export default function AuditReport() {
  const { id, token } = useParams();
  const location = useLocation();
  const isPublic = location.pathname.startsWith("/audit/public/");
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const req = isPublic
      ? axios.get(`${API}/audit/public/${id}`, { params: { token } })
      : api.get(`/audit-console/reports/${id}`);
    req
      .then(({ data }) => setReport(data))
      .catch((e) => setErr(e.response?.data?.detail || "Report not found"));
  }, [id, token, isPublic]);

  if (err) return <div className="p-16 text-center text-rose-700">{err}</div>;
  if (!report) return <div className="p-16 text-center text-neutral-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-neutral-100 print:bg-white" data-testid="audit-report-page">
      {/* Non-print toolbar */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10 print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <button onClick={() => window.history.back()} className="text-sm inline-flex items-center gap-1.5 text-neutral-600 hover:text-black">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button onClick={() => window.print()} className="lf-btn-primary text-sm inline-flex items-center gap-2" data-testid="audit-report-print-btn">
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto bg-white print:shadow-none shadow-lg my-8 print:my-0">
        {/* Cover */}
        <div className="p-10 border-b-4 border-[#E24E1B]">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <div className="font-display font-black text-lg tracking-tight">
                Clients<span className="text-[#E24E1B]">OnRamp</span>
              </div>
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mt-0.5">AI Visibility Audit</div>
            </div>
            <div className="text-right text-xs text-neutral-500">
              <div>{new Date(report.created_at).toLocaleDateString()}</div>
              <div>Engine: Perplexity Sonar</div>
            </div>
          </div>
          <h1 className="font-display text-5xl font-black tracking-tighter mb-2">{report.business_name}</h1>
          <div className="text-lg text-neutral-600">{report.industry} · {report.city}</div>
        </div>

        {/* Score band */}
        <div className="p-10 border-b border-neutral-200 bg-[#FFF6EE]">
          <div className="grid grid-cols-3 gap-6 items-center">
            <div className="col-span-1">
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">AI Visibility Score</div>
              <div className="font-display text-7xl font-black text-[#E24E1B] leading-none">
                {report.score.hits}<span className="text-3xl text-neutral-400">/{report.score.total}</span>
              </div>
              <div className="mt-2 text-sm font-bold">{report.score.grade}</div>
            </div>
            <div className="col-span-2 text-sm text-neutral-700 leading-relaxed">
              <p className="mb-3">
                We ran <strong>{report.score.total} real-world queries</strong> across the same web-connected
                AI engines your customers use every day (Perplexity, ChatGPT via Bing, Google AI Overviews).
              </p>
              <p>
                <strong>{report.business_name}</strong> was mentioned in <strong>{report.score.hits}</strong> of
                <strong> {report.score.total}</strong> responses ({report.score.pct}%). Below is the
                per-query breakdown, followed by the top 3 fixes that will move this number fastest.
              </p>
            </div>
          </div>
        </div>

        {/* Top 3 fixes */}
        <div className="p-10 border-b border-neutral-200">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">Recommended 30-day plan</div>
          <div className="space-y-4">
            {report.score.top_fixes.map((f, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="w-10 h-10 flex-shrink-0 bg-[#E24E1B] text-white flex items-center justify-center font-display font-black text-xl">
                  {i + 1}
                </div>
                <div>
                  <div className="font-bold text-lg">{f.title}</div>
                  <div className="text-sm text-neutral-600 leading-relaxed">{f.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Query-by-query breakdown */}
        <div className="p-10">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">
            Query-by-query results
          </div>
          <div className="space-y-4 print:break-inside-avoid">
            {report.results.map((r, i) => (
              <div key={i} className="border border-neutral-200 print:break-inside-avoid">
                <div className={`p-3 flex items-center justify-between gap-3 ${r.mention?.mentioned ? "bg-emerald-50" : "bg-rose-50"}`}>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Query {i + 1}</div>
                    <div className="font-mono text-sm font-semibold">{r.query}</div>
                  </div>
                  {r.mention?.mentioned ? (
                    <span className="text-xs font-semibold text-emerald-800 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Mentioned</span>
                  ) : (
                    <span className="text-xs font-semibold text-rose-800 inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Missing</span>
                  )}
                </div>
                {r.ok && (
                  <div className="p-3 text-xs text-neutral-700 whitespace-pre-wrap font-mono bg-white">
                    {r.answer || "(no answer)"}
                    {r.citations?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-neutral-100 flex flex-wrap gap-2">
                        {r.citations.slice(0, 5).map((c, ci) => (
                          <a key={ci} href={c} target="_blank" rel="noreferrer" className="text-[10px] text-blue-700 no-underline inline-flex items-center gap-0.5">
                            <ExternalLink className="w-2.5 h-2.5" /> {(() => { try { return new URL(c).hostname.replace("www.", ""); } catch { return c; } })()}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer / CTA */}
        <div className="p-10 border-t-4 border-[#0A192F] bg-[#0A192F] text-white">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="font-display text-2xl font-black leading-tight mb-2">Ready to move from {report.score.hits}/{report.score.total} → 8/10?</div>
              <div className="text-sm text-neutral-300 leading-relaxed">
                Our 30-day AI Visibility Accelerator ships all 3 fixes above, tracks progress
                weekly, and re-audits after 30 days so you have proof in your hand.
              </div>
            </div>
            <div className="text-sm text-neutral-300 flex flex-col gap-3">
              <div>
                <div className="mb-1 text-white font-semibold text-base">Book a strategy call</div>
                <div>Email: <a href={`mailto:${report.booking_email || "aiaudits@clientsonramp.com"}`} className="text-[#FF6B47] no-underline">{report.booking_email || "aiaudits@clientsonramp.com"}</a></div>
              </div>
              <a
                href={report.booking_url || "https://clientsonramp.com/book"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="report-book-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#E24E1B] hover:bg-[#FF6B47] text-white font-semibold px-5 py-3 no-underline transition print:hidden"
              >
                Schedule my 20-min call →
              </a>
              <div className="text-[11px] text-neutral-400">
                Pick any time on the calendar — we&apos;ll walk through your report together and map the fastest path to 8/10.
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 0.5in; size: letter; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
